import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip } from 'antd';
import { MoreOutlined, EyeOutlined, EditOutlined, KeyOutlined, LockOutlined, DeleteOutlined, MailOutlined, PlusOutlined } from '@ant-design/icons';
import { listUsers, updateUserStatus, deleteUser, resendActivation } from '../../services/users';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;
const { useApp } = App;

const UsersList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);


  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const { items, total } = await listUsers({
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        search: q || undefined,
        sortField: srt?.field,
        sortOrder: srt?.order,
      });
      setData(items || []);
      setPagination(prev => ({ 
        ...prev, 
        current: page || prev.current, 
        pageSize: pageSize || prev.pageSize, 
        total,
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de usuarios.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, value || '');
  };

  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    
    switch (action) {
      case 'view':
        navigate(`/users/view/${record.id}`);
        break;
      case 'edit':
        navigate(`/users/${record.id}/edit`);
        break;
      case 'changePassword':
        navigate(`/users/${record.id}/change-password`);
        break;
      case 'toggle':
        const willActivate = !record.isActive;
        modal.confirm({
          title: willActivate ? '¿Activar usuario?' : '¿Desactivar usuario?',
          content: `${record.name || record.email || 'Usuario'}`,
          okText: willActivate ? 'Activar' : 'Desactivar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await updateUserStatus(record.id, willActivate);
              message.success(willActivate ? 'Usuario activado' : 'Usuario desactivado');
              fetchData(pagination.current, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo actualizar el estatus');
            }
          },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar usuario?',
          content: `${record.name || record.email || 'Usuario'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deleteUser(record.id);
              message.success('Usuario eliminado');

              const nextPage = (data.length === 1 && pagination.current > 1)
                ? pagination.current - 1
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo eliminar el usuario');
            }
          },
        });
        break;
      case 'resendInvitation':
        modal.confirm({
          title: '¿Reenviar invitación?',
          content: `Se enviará un correo de activación a ${record.email || 'el usuario'}`,
          okText: 'Reenviar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await resendActivation(record.id);
              message.success('Invitación reenviada correctamente');
            } catch {
              message.error('No se pudo reenviar la invitación');
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Ver usuario',
      },
      can('users.edit') &&
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar usuario',
      },
      can('users.change_password') && {
        key: 'changePassword',
        icon: <KeyOutlined />,
        label: 'Cambiar contraseña',
      },
      {
        type: 'divider',
      },
      can('users.toggle') && {
        key: 'toggle',
        icon: <LockOutlined />,
        label: record.isActive ? 'Desactivar' : 'Activar',
      },
      can('users.delete') && {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Borrar usuario',
        danger: true,
      },
      {
        type: 'divider',
      },
      can('users.resend_invite') && {
        key: 'resendInvitation',
        icon: <MailOutlined />,
        label: 'Reenviar invitación',
      },
    ].filter(Boolean);

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    { title: 'Nombre Usuario', dataIndex: 'firstName', key: 'firstName', sorter: true, render: (text, record) => {
        return `${record.firstName} ${record.lastName} ${record.secondLastName ? `${record.secondLastName}` : ''}`;
      },
    },
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: true },
    { title: 'Rol', dataIndex: 'role', key: 'role', sorter: true, render: (role) => role?.description || role?.name || '-', },
    {
      title: 'Estatus',
      dataIndex: 'isActive',
      key: 'isActive',
      sorter: true,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'volcano'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
      // filters: [
      //   { text: 'Activo', value: '1' },
      //   { text: 'Inactivo', value: '0' },
      // ],
      // onFilter: (value, record) => String(record.is_active ? 1 : 0) === String(value),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>
          Usuarios
        </Title>

        {can('users.add') && (<Tooltip title="Agregar usuario">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className='btn-add'
            aria-label="Agregar usuario"
            onClick={() => navigate('/users/add')}
          />
        </Tooltip>)}
      </div>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Buscar por nombre o email"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          />
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Total: ${total} usuarios`
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default UsersList;
